/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { IServerSideGetRowsParams } from 'ag-grid-community';
import { set } from 'lodash';

import { getDateFilter } from '@/utils/getDateFilter';
import { getOperator } from '@/utils/getOperator';

interface IDataSourceProps {
  query: any;
  variables?: any;
  skip?: boolean;
  searchBy: string;
  searchTerm: string;
  ownerId?: string;
  gridRef?: React.RefObject<any>;
  refetchTrigger?: boolean;
  persistColumnOrder?: boolean;
}

export function useAdvancedSireDataSource({
  query,
  variables,
  skip,
  searchBy,
  searchTerm,
  ownerId,
  gridRef,
  refetchTrigger,
  persistColumnOrder = false,
}: IDataSourceProps) {
  const apiRef = useRef<{ api: any } | null>(null);

  const getRows = useCallback(
    ({ request, success, fail: _fail, api }: IServerSideGetRowsParams) => {
      if (
        skip ||
        typeof request.startRow === 'undefined' ||
        typeof request.endRow === 'undefined'
      ) {
        success({
          rowData: [],
          rowCount: 0,
        });
        return;
      }

      apiRef.current = { api };

      const order = request.sortModel
        .map((s) => ({
          sort: s.sort,
          field: api.getColumn(s.colId)?.getColDef().field,
        }))
        .filter((s) => s.field)
        .map((s) => set({}, s.field!, s.sort === 'asc' ? 'ASC' : 'DESC'));

      const filterModel = request.filterModel;
      const filterConditions: any[] = [];

      Object.entries(filterModel || {}).forEach(([field, condition]) => {
        const [parent, child] = field.split('.');

        if (parent === 'breed') {
          const isSetFilter = condition.filterType === 'set';
          if (child === 'subFamily' && isSetFilter) {
            filterConditions.push({
              or: condition.values.map((v: string) => ({
                breed: { subFamily: { eq: v } },
              })),
            });
          } else {
            filterConditions.push({
              breed: {
                [child ?? 'code']: {
                  [getOperator(condition.type)]: condition.filter,
                },
              },
            });
          }
          return;
        }

        if (field === 'heparin') {
          filterConditions.push({
            heparin: {
              [getOperator(condition.type)]: Number(condition.filter),
            },
          });
          return;
        }

        if (
          field === 'birth' ||
          field === 'createdOn' ||
          field === 'modifiedOn'
        ) {
          filterConditions.push(getDateFilter(field, condition));
          return;
        }

        filterConditions.push(
          child
            ? {
                [parent]: {
                  [child]: {
                    [getOperator(condition.type)]: condition.filter,
                  },
                },
              }
            : {
                [field]: {
                  [getOperator(condition.type)]: condition.filter,
                },
              },
        );
      });

      const searchCondition =
        searchBy.length === 0
          ? {
              or: [
                { name: { contains: searchTerm } },
                { breed: { code: { contains: searchTerm } } },
                { breedReg: { contains: searchTerm } },
                { registrationNumber: { contains: searchTerm } },
                { regPrefix: { contains: searchTerm } },
                { cane: { contains: searchTerm } },
                { sire: { contains: searchTerm } },
              ],
            }
          : searchBy.includes('.')
            ? {
                [searchBy.split('.')[0]]: {
                  [searchBy.split('.')[1]]: { contains: searchTerm },
                },
              }
            : searchBy === 'heparin'
              ? { heparin: { eq: Number(searchTerm) } }
              : { [searchBy]: { contains: searchTerm } };

      const where = {
        and: [
          ...filterConditions,
          searchCondition,
          ownerId ? { ownerId: { eq: ownerId } } : null,
        ].filter(Boolean),
      };

      query({
        skip: request.startRow,
        take: request.endRow - request.startRow,
        order,
        where,
        ...variables,
      })
        .unwrap()
        .then((res: any) => {
          const result = {
            rowData: res.sires.items,
            rowCount: res.sires.totalCount,
          };
          if (result.rowCount === 0 && gridRef?.current?.api) {
            gridRef.current.api.showNoRowsOverlay();
          } else if (result.rowCount > 0 && gridRef?.current?.api) {
            gridRef.current.api.hideOverlay();
          }

          success(result);
        });
    },
    [
      ownerId,
      query,
      searchBy,
      searchTerm,
      skip,
      variables,
      gridRef,
      refetchTrigger,
    ],
  );

  const refetch = useCallback(() => {
    if (apiRef.current?.api) {
      apiRef.current.api.refreshServerSide({ purge: true });
    }
  }, []);

  // When persistColumnOrder is enabled, use a stable getRows wrapper so the
  // serverSideDatasource prop never changes reference. This prevents AG Grid
  // from reinitializing the row model (which would reset column sort state)
  // every time the search term or other filters change.
  // The wrapper delegates to a ref that always holds the latest getRows, so
  // the query logic stays up-to-date even though the callback reference is stable.
  const getRowsRef = useRef(getRows);
  useEffect(() => {
    getRowsRef.current = getRows;
  }, [getRows]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableGetRows = useCallback(
    (params: IServerSideGetRowsParams) => getRowsRef.current(params),
    [],
  );

  const activeGetRows = persistColumnOrder ? stableGetRows : getRows;

  useEffect(() => {
    if (persistColumnOrder) {
      refetch();
    }
  }, [searchBy, searchTerm, ownerId, refetch, persistColumnOrder]);

  return useMemo(
    () => ({
      dataSource: { getRows: activeGetRows },
      refetch,
    }),
    [activeGetRows, refetch],
  );
}
